import React from 'react';
import { COLORS } from '@/components/ui/ui.constants';
import Icon from '@/components/ui/Icon';

interface SyncStatusIconProps {
  status: 'success' | 'in_progress' | 'failed' | 'pending';
}

// AIDEV-NOTE: PLAT-18137/18052 — `pending` means no sync has been attempted for this score, which is
// neither a success nor a failure. It is the bare cloud in dark-grey (#777770, "Gray 700" in Figma):
// the three states are told apart by shape, not colour, so the legend still reads at WCAG AA and to
// anyone who can't distinguish the greys. Keeping the cloud empty also separates it from
// `in_progress`, whose three dots are the transient "you just clicked sync" spinner.
export const SYNC_STATUS_ICON_MAP = {
  failed: 'CloudFailed',
  in_progress: 'CloudInProgress',
  pending: 'CloudPending',
  success: 'CloudSuccess',
} as const;

const FILL_MAP = {
  failed: COLORS.red,
  in_progress: COLORS['med-dark-grey'],
  pending: COLORS['dark-grey'],
  success: COLORS.green,
} as const;

// AIDEV-NOTE: CloudInProgress draws three dots to read as "working", but the SVG is static and
// nothing ever animated it, so the state looked identical to a finished one. Blinking the dots —
// every path but the cloud outline — out of phase gives the typing-indicator effect the glyph
// implies. Only in_progress animates; the other three are resting states.
// The keyframe is sync-dot rather than Tailwind's animate-pulse, which only dips to 50% opacity over
// two seconds and is invisible at this size — see tailwind.config.js.
// nth-child(n+2) rather than :not(:first-child) — Tailwind's extractor reads these class strings
// verbatim out of the source, and nested parens in an arbitrary variant are the fragile case.
// The delays are a third and two thirds of one dot's "lit" window, so the wave travels rather than
// the three of them blinking together.
export const IN_PROGRESS_ANIMATION = [
  '[&>path:nth-child(n+2)]:tw-animate-sync-dot',
  '[&>path:nth-child(3)]:[animation-delay:250ms]',
  '[&>path:nth-child(4)]:[animation-delay:500ms]',
  // A high-contrast blink is exactly what vestibular sensitivity reacts to. The glyph's three
  // dots already distinguish this state from the other three, so standing still costs nothing.
  'motion-reduce:[&>path:nth-child(n+2)]:tw-animate-none',
].join(' ');

export const SyncStatusIcon = ({ status }: SyncStatusIconProps) => (
  <Icon
    className={status === 'in_progress' ? IN_PROGRESS_ANIMATION : undefined}
    fill={FILL_MAP[status]}
    icon={SYNC_STATUS_ICON_MAP[status]}
    size={20}
  />
);
